import EditIcon from '@mui/icons-material/Edit';
import { CircularProgress, Grid, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { components } from 'react-select';
import { ErrorAlert } from '../../components/atoms/Alert-message';
import Button from '../../components/atoms/button';
import CustomSelect from '../../components/atoms/custom-select';
import InputTextAreaField from '../../components/atoms/input-field/inputTextAreaField';
import InputTextField from '../../components/atoms/input-field/inputTextField';
import Toast, {
  showToasterFailure,
  showToasterSuccess,
} from '../../components/atoms/toast';
import { APIHeader } from '../../index';
import { createGetParticularOverviewOptions } from '../../queries/overviewQueries';
import { errorNotification } from '../../reducers/handle-api-status/slice';
import {
  LocalSiteButtons,
  LocalSiteComment,
  LocalSiteDropdown,
  ReviewDoneMessage,
  SiteImpactedDropdown,
  SiteReviewTitle,
  SiteReviewValue,
  TextAsLink,
  UploadedDocument,
  boldFont,
  buttonCircularProgress,
  commentStyle,
  descriptionLSC,
  descriptionLSCGrid,
  editLSC,
  hidden,
  homeButtonComponent,
  lscDescriptionValue,
  noFileText,
  titleName,
  uploadedFilesGrid,
  validationError,
  validationErrorOnPages,
} from '../../styles';
import {
  getDownloadLink,
  getURL,
  isEmptyObject,
  stripTrailingSlash,
  validateInputFields,
} from '../../utils';

const LocalSiteReview = props => {
  const { apiConfig, userInfo, errorInAPI } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [siteList, setSiteList] = useState([]);
  const [SCEmails, setSCEmails] = useState([]);
  const [localSiteData, setLocalSiteData] = useState({});
  const [plantsImpacted, setPlantsImpacted] = useState('');
  const [disabled, setDisabled] = useState(true);
  const [isEdit, setIsEdit] = useState({
    isTitleClicked: false,
    isDescriptionClicked: false,
  });
  const [displayFiles, setDisplayFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValidationMessage, setInputValidationMessage] = useState({
    title: '',
    description: '',
    comments: '',
  });
  const [showProgress, setShowProgress] = useState(false);

  let ownerId = null;
  const pecrId = stripTrailingSlash(window.location.toString())
    .split('/')
    .pop();
  const url = getURL();
  const hasNotValidationError =
    inputValidationMessage.title === '' &&
    inputValidationMessage.description === '' &&
    inputValidationMessage.comments === '';
  const { data, isError, error } = useQuery(
    createGetParticularOverviewOptions(
      {
        pecrId,
      },
      { staleTime: 1000 * 60 * 20 } // 20 minute stale time
    )
  );
  console.log('ownerid', data);
  if (data && data[0] && !isError) {
    ownerId = data.UserId;
  }
  const localSite = () => {
    //To get the data of local site coordinator page
    fetch(`${apiConfig.uiURL}api/LocalSiteCoordinator`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        Id: pecrId,
      }),
    })
      .then(async response => {
        const data = await response.json();
        //stop showing loader
        setIsLoading(false);
        setLocalSiteData(data);
        if (data.StatusID === 2) {
          setDisabled(false);
        } else {
          setDisabled(true);
        }
        if (data.SitesImpacted === '') {
          setPlantsImpacted(data.CreatorSiteId + '');
        } else {
          setPlantsImpacted(data.SitesImpacted + '');
        }
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });

    //To fetch Files the from Folder uploaded on Overview screen
    fetch(`${apiConfig.uiURL}api/FetchFiles`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        MainFolder: 'PECROverview',
        SubFolder: pecrId,
      }),
    })
      .then(async response => {
        const files = await response.json();
        const filterFilesOnly = files.filter(file => {
          return file.includes('.');
        });
        setDisplayFiles(filterFilesOnly);
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  useEffect(() => {
    if (pecrId) {
      fetch(`${apiConfig.uiURL}api/SiteList`, {
        headers: APIHeader(),
      })
        .then(async response => {
          const data = await response.json();
          setSiteList(data);
        })
        .catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
      localSite();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pecrId, apiConfig]);

  useEffect(() => {
    if (plantsImpacted && pecrId) {
      fetch(`${apiConfig.uiURL}api/SC`, {
        method: 'POST',
        headers: APIHeader(),
        body: JSON.stringify({
          PlantsImpacted: plantsImpacted,
          pecrId: pecrId,
        }),
      })
        .then(async response => {
          const data = await response.json();
          setSCEmails(data);
        })
        .catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantsImpacted, pecrId, apiConfig]);

  //To get the User who is assigned as Site Coordinator for particular PECR/Site
  //Page will be accessible to validLSCUser only
  const validLSCUser =
    (SCEmails.length > 0 &&
      !isEmptyObject(localSiteData) &&
      SCEmails.find(
        item => Number(item.SiteID) === Number(localSiteData.SiteID)
      )) ||
    {};

  //To get options to select the sites
  const siteOptions =
    !isEmptyObject(localSiteData) &&
    localSiteData.CreatorSiteId &&
    siteList?.map(list => {
      if (list.ID === localSiteData.CreatorSiteId) {
        return { label: list.SiteName, value: list.ID, isFixed: true };
      } else {
        return { label: list.SiteName, value: list.ID };
      }
    });

  //To handle the onChange of site impacted drop-down
  const handleSiteChange = (sites, actionMeta) => {
    if (hasNotValidationError) {
      if (sites.length > 0) {
        //show loader
        setIsLoading(true);
        const selectedSites = sites
          .map(site => {
            return site.value;
          })
          .join();
        setPlantsImpacted(selectedSites);
        //Call Api to save the drop-down value
        fetch(`${apiConfig.uiURL}api/UpdateOverViewFromLSC`, {
          method: 'PUT',
          headers: APIHeader(),
          body: JSON.stringify({
            Id: pecrId,
            Title: localSiteData.Title,
            Description: localSiteData.Description,
            siteImpacted: selectedSites,
            Comments: localSiteData.Comments,
          }),
        })
          .then(async () => {
            localSite();
          })
          .catch(() => {
            dispatch(
              errorNotification({
                hasErrorOccured: true,
                message: t('error_message'),
              })
            );
          });
      } else {
        setPlantsImpacted(localSiteData.CreatorSiteId + '');
      }
      if (sites.length === 0) {
        const remainingSites = actionMeta.removedValues.filter(item => {
          return item.isFixed;
        });
        setIsLoading(true);
        fetch(`${apiConfig.uiURL}api/UpdateOverViewFromLSC`, {
          method: 'PUT',
          headers: APIHeader(),
          body: JSON.stringify({
            Id: pecrId,
            Title: localSiteData.Title,
            Description: localSiteData.Description,
            siteImpacted: remainingSites[0].value + '',
            Comments: localSiteData.Comments,
          }),
        })
          .then(async () => {
            localSite();
          })
          .catch(() => {
            dispatch(
              errorNotification({
                hasErrorOccured: true,
                message: t('error_message'),
              })
            );
          });
      }
    }
  };

  //To handle the onClick functionality of Cancel Button
  const onCancelClick = () => {
    fetch(`${apiConfig.uiURL}api/lscCancel`, {
      headers: APIHeader(),
      method: 'POST',
      body: JSON.stringify({
        statusId: 7,
        pecrId: pecrId,
        comments: localSiteData.Comments,
        lscId: userInfo.userID,
      }),
    })
      .then(async response => {
        fetch(`${apiConfig.uiURL}api/sendEmail`, {
          headers: APIHeader(),
          method: 'POST',
          body: JSON.stringify({
            PecrId: pecrId,
            toAddress: localSiteData.Email,
            loginUserEmail: userInfo.userEmail,
            Title: localSiteData.Title,
            Description: '',
            fromAddress: 'PECR@carrier.com',
            type: 'assignEmail',
            URL: '',
            Factory: '',
            Strategic: '',
            EHSI: '',
            RI: '',
            ECI: '',
            TA: '',
            WCI: '',
            PPS: '',
            toWhome: 'Local Site Coordinator Cancel',
            Comments:
              localSiteData && localSiteData.Comments
                ? localSiteData.Comments
                : '',
            creator: `${localSiteData.FirstName} ${localSiteData.LastName}`,
            creatorsite: localSiteData && localSiteData.CreatorSiteName,
          }),
        }).catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
    navigate('../');
  };

  //To handle the onClick functionality of Send back to creator Button
  const onBackToCreatorClick = () => {
    fetch(`${apiConfig.uiURL}api/lscSendBackToCreator`, {
      headers: APIHeader(),
      method: 'POST',
      body: JSON.stringify({
        statusId: 1,
        pecrId: pecrId,
        sitesImpacted: '',
        comments: localSiteData.Comments,
        lscId: userInfo.userID,
      }),
    })
      .then(async response => {
        fetch(`${apiConfig.uiURL}api/sendEmail`, {
          headers: APIHeader(),
          method: 'POST',
          body: JSON.stringify({
            PecrId: pecrId,
            toAddress: localSiteData.Email,
            loginUserEmail: userInfo.userEmail,
            Title: localSiteData.Title,
            Description: '',
            fromAddress: 'PECR@carrier.com',
            type: 'assignEmail',
            URL: `${url}Overview/${pecrId}`,
            Factory: '',
            Strategic: '',
            EHSI: '',
            RI: '',
            ECI: '',
            TA: '',
            WCI: '',
            PPS: '',
            toWhome: 'Local Site Coordinator Back to Creator',
            Comments:
              localSiteData && localSiteData.Comments
                ? localSiteData.Comments
                : '',
            creator: `${localSiteData.FirstName} ${localSiteData.LastName}`,
            creatorsite: localSiteData && localSiteData.CreatorSiteName,
          }),
        }).catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });

    navigate('../');
  };

  //To handle the onClick functionality of Save Button
  const onSaveClick = () => {
    setShowProgress(true);
    const updateData = fetch(`${apiConfig.uiURL}api/UpdateOverViewFromLSC`, {
      method: 'PUT',
      headers: APIHeader(),
      body: JSON.stringify({
        Id: pecrId,
        Title: localSiteData.Title,
        Description: localSiteData.Description,
        siteImpacted: plantsImpacted,
        Comments: localSiteData.Comments,
      }),
    })
      .then(() => {
        setShowProgress(false);
      })
      .catch(() => {
        setShowProgress(false);
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
    Promise.all([updateData])
      .then(() => {
        showToasterSuccess(t('Save_message'));
      })
      .catch(() => {
        showToasterFailure(t('Save_failed_message'));
      });
  };

  //To handle the onClick functionality of Submit Button
  const onSubmitClick = () => {
    fetch(`${apiConfig.uiURL}api/lscSubmit`, {
      headers: APIHeader(),
      method: 'POST',
      body: JSON.stringify({
        statusId: 3,
        pecrId: pecrId,
        sitesImpacted: plantsImpacted,
        comments: localSiteData.Comments,
        lscId: userInfo.userID,
      }),
    })
      .then(async response => {
        SCEmails.forEach(item => {
          return (
            fetch(`${apiConfig.uiURL}api/InsertPECRDepartments`, {
              headers: APIHeader(),
              method: 'POST',
              body: JSON.stringify({
                pecrId: pecrId,
                siteId: item.SiteID,
              }),
            }).catch(() => {
              dispatch(
                errorNotification({
                  hasErrorOccured: true,
                  message: t('error_message'),
                })
              );
            }),
            fetch(`${apiConfig.uiURL}api/CreateFolderBasedOnSite`, {
              headers: APIHeader(),
              method: 'POST',
              body: JSON.stringify({
                MainFolder: 'PECROverview',
                pecrId: pecrId,
                siteId: item.SiteID,
              }),
            }).catch(() => {
              dispatch(
                errorNotification({
                  hasErrorOccured: true,
                  message: t('error_message'),
                })
              );
            }),
            fetch(`${apiConfig.uiURL}api/sendEmail`, {
              headers: APIHeader(),
              method: 'POST',
              body: JSON.stringify({
                PecrId: pecrId,
                toAddress: item.Email,
                loginUserEmail: userInfo.userEmail,
                Title: localSiteData.Title,
                Description: localSiteData.Description,
                fromAddress: 'PECR@carrier.com',
                type: 'assignEmail',
                URL: `${url}SiteReview/${pecrId}/${item.SiteID}`,
                Factory: item.SiteName,
                Strategic: localSiteData.Strategic,
                EHSI: localSiteData.Safety,
                RI: localSiteData.Regulatory,
                ECI: localSiteData.CustomerImpact,
                TA: localSiteData.Turnback,
                WCI: localSiteData.Warranty,
                PPS: localSiteData.StopProduction,
                STD: localSiteData.Standardization,
                CR: localSiteData.CostReduction,
                toWhome: 'Local Site Coordinator Submit',
                Comments:
                  localSiteData && localSiteData.Comments
                    ? localSiteData.Comments
                    : '',
                creator: `${localSiteData.FirstName} ${localSiteData.LastName}`,
                creatorsite: localSiteData && localSiteData.CreatorSiteName,
              }),
            }).catch(() => {
              dispatch(
                errorNotification({
                  hasErrorOccured: true,
                  message: t('error_message'),
                })
              );
            })
          );
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
    navigate('../');
  };

  const onEditClick = label => {
    if (label === 'description') {
      setIsEdit({
        isDescriptionClicked: true,
        isTitleClicked: false,
      });
    } else if (label === 'title') {
      setIsEdit({
        isTitleClicked: true,
        isDescriptionClicked: false,
      });
    }
  };
  //To update the Title and description
  const onBlurChange = (event, inputType) => {
    if (hasNotValidationError) {
      let blurTitle = localSiteData.Title;
      let blurDescription = localSiteData.Description;
      if (inputType === 'description') {
        blurDescription = event.target.value.trim();
      } else if (inputType === 'title') {
        blurTitle = event.target.value.trim();
      }
      setIsEdit({
        isDescriptionClicked: false,
        isTitleClicked: false,
      });
      setLocalSiteData({
        ...localSiteData,
        Title: blurTitle,
        Description: blurDescription,
      });
      fetch(`${apiConfig.uiURL}api/UpdateOverViewFromLSC`, {
        method: 'PUT',
        headers: APIHeader(),
        body: JSON.stringify({
          Id: pecrId,
          Title: blurTitle,
          Description: blurDescription,
          siteImpacted: plantsImpacted,
          Comments: localSiteData.Comments,
        }),
      }).catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
    } else {
      setIsEdit({
        isDescriptionClicked: false,
        isTitleClicked: false,
      });
      setInputValidationMessage({
        ...inputValidationMessage,
        title: '',
        description: '',
      });
    }
  };

  //to handle the onChange of title, description & comment
  const onInputChange = (event, key) => {
    const eventValue = event.target.value.trim();
    if (key === 'comments') {
      setLocalSiteData({
        ...localSiteData,
        Comments: eventValue,
      });
    }
    const ValidationMessage = validateInputFields(eventValue, t);
    setInputValidationMessage({
      ...inputValidationMessage,
      [key]: ValidationMessage,
    });
  };

  //To call the UpdateOverViewFromLSC API once User stops typing comments
  useEffect(() => {
    if (
      hasNotValidationError &&
      !isEmptyObject(localSiteData) &&
      plantsImpacted !== ''
    ) {
      const timer = setTimeout(() => {
        fetch(`${apiConfig.uiURL}api/UpdateOverViewFromLSC`, {
          method: 'PUT',
          headers: APIHeader(),
          body: JSON.stringify({
            Id: pecrId,
            Title: localSiteData.Title,
            Description: localSiteData.Description,
            siteImpacted: plantsImpacted,
            Comments: localSiteData.Comments,
          }),
        }).catch(() => {
          dispatch(
            errorNotification({
              hasErrorOccured: true,
              message: t('error_message'),
            })
          );
        });
      }, 500);
      return () => clearTimeout(timer);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSiteData.Comments]);

  const Productfamily =
    !isEmptyObject(localSiteData) &&
    localSiteData.Name &&
    localSiteData.Name.split(',').join(', ');

  const selectedSites =
    !isEmptyObject(localSiteData) &&
    localSiteData.Sites &&
    localSiteData.Sites.split(',').map(el => el.trim());

  const sitesImpactedValue =
    siteOptions &&
    siteOptions.length > 0 &&
    siteOptions.filter(site =>
      selectedSites && selectedSites.length > 0
        ? selectedSites.includes(site.label)
        : plantsImpacted.includes(site.value)
    );

  const defaultValue =
    siteOptions &&
    siteOptions.length > 0 &&
    siteOptions.find(site => site.isFixed);

  const MultiValueRemove = props => {
    if (props.data.isFixed) {
      return null;
    }
    return <components.MultiValueRemove {...props} />;
  };

  //To download the files on DownloadFilesForLSC API click
  const onDownloadClick = filename => {
    fetch(`${apiConfig.uiURL}api/DownloadFilesForLSC`, {
      method: 'POST',
      headers: APIHeader(),
      body: JSON.stringify({
        MainFolder: 'PECROverview',
        SubFolder: pecrId,
        filename: filename,
      }),
    })
      .then(async response => {
        response.blob().then(blob => {
          getDownloadLink(blob, filename);
        });
      })
      .catch(() => {
        dispatch(
          errorNotification({
            hasErrorOccured: true,
            message: t('error_message'),
          })
        );
      });
  };

  const disableBackToCreatorORCancel =
    disabled ||
    localSiteData.Comments === '' ||
    localSiteData.Comments === null ||
    localSiteData.Title === '' ||
    localSiteData.Title === null ||
    localSiteData.Description === '' ||
    localSiteData.Description === null;

  return errorInAPI.hasErrorOccured ? (
    <ErrorAlert message={errorInAPI.message} />
  ) : siteList.length > 0 &&
    userInfo &&
    userInfo.userId !== '' &&
    !isLoading ? (
    !isEmptyObject(validLSCUser) &&
    userInfo?.active?.toString() === '1' &&
    userInfo?.userId?.toString() !== '0' &&
    userInfo?.userId !== null &&
    Number(userInfo?.userID) === Number(validLSCUser.ID) ? (
      <Grid>
        {disabled && (
          <Grid style={ReviewDoneMessage}>
            {t('Local_Site_Coordinator_has_already_reviewed_the_PECR')}
          </Grid>
        )}
        <Grid style={SiteReviewTitle}>
          <h4 style={titleName}>{t('Local_Site_Coordinator_Review')}</h4>
        </Grid>
        <Grid>
          <Grid style={SiteReviewTitle}>
            <Grid style={boldFont}>{t('Title')}:</Grid>
            {isEdit.isTitleClicked ? (
              <Grid style={descriptionLSCGrid}>
                <InputTextField
                  placeholder={t('Enter_PECR_title_here')}
                  name={'Title'}
                  onBlur={event => onBlurChange(event, 'title')}
                  onChange={event => {
                    onInputChange(event, 'title');
                  }}
                  value={localSiteData.Title}
                  autoFocus={true}
                  maxLength={'100'}
                />
                <Grid style={validationError}>
                  {inputValidationMessage.title}
                </Grid>
              </Grid>
            ) : (
              <Grid style={SiteReviewValue}>
                <span>{localSiteData.Title}</span>
                <span>{` (${pecrId})`}</span>
                {!disabled && (
                  <EditIcon
                    fontSize="small"
                    style={isEdit.isTitleClicked ? hidden : editLSC}
                    onClick={() => onEditClick('title')}
                  />
                )}
              </Grid>
            )}
          </Grid>
          <Grid style={SiteReviewTitle}>
            <Grid style={boldFont}>{t('Description')}:</Grid>
            {isEdit.isDescriptionClicked ? (
              <Grid style={descriptionLSCGrid}>
                <InputTextAreaField
                  placeholder={t('Enter_PECR_description_here')}
                  name={'Description'}
                  onBlur={event => onBlurChange(event, 'description')}
                  onChange={event => {
                    onInputChange(event, 'description');
                  }}
                  value={localSiteData.Description}
                  customStyle={descriptionLSC}
                  autoFocus={true}
                  maxLength={'1000'}
                />
                <Grid style={validationError}>
                  {inputValidationMessage.description}
                </Grid>
              </Grid>
            ) : (
              <Grid style={lscDescriptionValue}>
                {localSiteData.Description}
                {!disabled && (
                  <EditIcon
                    fontSize="small"
                    style={isEdit.isDescriptionClicked ? hidden : editLSC}
                    onClick={() => onEditClick('description')}
                  />
                )}
              </Grid>
            )}
          </Grid>
          <Grid style={SiteReviewTitle}>
            <Grid style={boldFont}>{t('Product_Family')}:</Grid>
            <Grid style={SiteReviewValue}>{Productfamily}</Grid>
          </Grid>
          <Grid style={SiteReviewTitle}>
            <Grid style={boldFont}>{t('Creator')}:</Grid>
            <Grid
              style={SiteReviewValue}
            >{`${localSiteData.FirstName} ${localSiteData.LastName} (${localSiteData.CreatorSiteName})`}</Grid>
          </Grid>
          <Grid style={SiteImpactedDropdown}>
            <Grid style={boldFont}>*{t('Sites_Impacted')}:</Grid>
            <Grid style={LocalSiteDropdown}>
              <CustomSelect
                name={'plantsImpacted'}
                id={'PlantsImpacted'}
                placeholder={!disabled ? t('Select_Plants_Impacted') : null}
                options={siteOptions}
                isMulti
                onChange={handleSiteChange}
                isDisabled={disabled}
                value={
                  sitesImpactedValue.length > 0
                    ? sitesImpactedValue
                    : defaultValue
                }
                components={{ MultiValueRemove }}
              />
            </Grid>
          </Grid>
          {
            <Grid style={SiteReviewTitle}>
              <Grid style={uploadedFilesGrid}>
                <Grid style={UploadedDocument}>{t('Uploaded_Document')}:</Grid>
                <Grid style={UploadedDocument}>
                  {displayFiles.length > 0 ? (
                    displayFiles.map((document, index) => {
                      return (
                        <Tooltip
                          key={index}
                          title={document}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                fontSize: '14px',
                              },
                            },
                          }}
                        >
                          <Typography
                            key={index}
                            noWrap
                            width={300}
                            style={TextAsLink}
                            onClick={() => onDownloadClick(document)}
                          >
                            {document}
                          </Typography>
                        </Tooltip>
                      );
                    })
                  ) : (
                    <Grid style={noFileText}>
                      {t('No_files_have_been_uploaded')}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          }
          <Grid style={LocalSiteComment}>
            <InputTextAreaField
              name={'LCRComment'}
              placeholder={!disabled ? t('Add_Comments_here') : ''}
              onChange={event => onInputChange(event, 'comments')}
              customStyle={commentStyle}
              disabled={disabled}
              value={localSiteData.Comments}
            />
          </Grid>
          <Grid style={validationErrorOnPages}>
            {inputValidationMessage.comments}
          </Grid>
        </Grid>
        <Toast />
        <Grid style={LocalSiteButtons}>
          <Button
            title={t('Cancel')}
            onClick={onCancelClick}
            disabled={disableBackToCreatorORCancel || !hasNotValidationError}
          />
          <Button
            title={t('Send_back_to_Creator')}
            onClick={onBackToCreatorClick}
            disabled={disableBackToCreatorORCancel || !hasNotValidationError}
          />
          <Grid sx={{ position: 'relative' }}>
            <Button
              title={t('Save')}
              onClick={onSaveClick}
              disabled={disabled || !hasNotValidationError || showProgress}
            />
            {showProgress && (
              <CircularProgress size={28} sx={buttonCircularProgress} />
            )}
          </Grid>
          <Button
            title={t('Submit')}
            onClick={onSubmitClick}
            disabled={
              plantsImpacted === '' ||
              disabled ||
              localSiteData.Title === '' ||
              localSiteData.Title === null ||
              localSiteData.Description === '' ||
              localSiteData.Description === null ||
              !hasNotValidationError
            }
          />
        </Grid>
      </Grid>
    ) : (
      <Grid style={homeButtonComponent}>{t('No_active_user_message')}</Grid>
    )
  ) : (
    <Grid style={homeButtonComponent}>
      <CircularProgress />
    </Grid>
  );
};

const mapStateToProps = (state = {}) => {
  return {
    userInfo: state.userInfo,
    errorInAPI: state.errorNotification,
  };
};

export default connect(mapStateToProps, null)(LocalSiteReview);
